<template>
  <div class="music-page">
    <!-- 顶部标题 -->
    <div class="page-header">
      <div class="header-left">
        <i class="fas fa-music"></i>
        <div>
          <h2>音乐控制台</h2>
          <span class="sub">开始音乐固定播放 · 结束音乐随机抽取</span>
        </div>
      </div>
      <div class="header-right">
        <span class="badge"><i class="fas fa-circle-info"></i> 数据存储在本地，完全离线</span>
        <span class="fade-settings">
          <i class="fas fa-wave-square"></i>
          <span class="fade-label">淡入淡出</span>
          <el-switch v-model="fadeEnabled" active-color="#b45309" size="mini"></el-switch>
          <el-input-number
            v-model="fadeDuration"
            :min="0.5"
            :max="10"
            :step="0.5"
            :disabled="!fadeEnabled"
            size="mini"
            controls-position="right"
            class="fade-duration-input"></el-input-number>
          <span class="fade-unit">秒</span>
        </span>
      </div>
    </div>

    <div class="main-row">
        <!-- 左下：开始音乐 -->
        <el-card class="card start-card" shadow="hover">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="fas fa-play-circle"></i>
              <span>开始音乐</span>
              <el-tag size="mini" type="warning" effect="plain">按时段</el-tag>
            </div>
            <div class="scheduler-toggle">
              <el-tooltip :content="schedulerEnabled ? '已开启自动调度' : '已关闭自动调度'" placement="top">
                <el-switch v-model="schedulerEnabled" active-color="#b45309" @change="onSchedulerToggle"></el-switch>
              </el-tooltip>
              <span class="now-time">{{ nowHHmmss }}</span>
            </div>
          </div>

          <!-- 当前状态信息面板 -->
          <!-- <div class="period-info-panel">
            <div class="info-row">
              <span class="info-label"><i class="fas fa-sun"></i> 当前时段</span>
              <span class="info-value">
                <el-tag size="mini" :type="periodTagType(currentPeriod.key)" effect="dark">
                  {{ currentPeriod.label }}
                </el-tag>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label"><i class="fas fa-headphones"></i> 当前播放音乐</span>
              <span class="info-value" :class="{ playing: startPlaying }">{{ currentPlayingName }}</span>
            </div>
          </div> -->

          <!-- 时段配置表单：只显示当前时段（按当前时间自动选取） -->
          <el-form size="mini" label-position="left" class="period-form">
            <div class="period-form-item active">
              <div class="pf-head">
                <!-- <el-tag size="mini" :type="periodTagType(currentPeriod.key)" effect="dark">{{ currentPeriod.label }}</el-tag> -->
                <!-- <span class="pf-hint">当前时段（按当前时间自动选取）</span> -->
                <div class="pf-actions">
                  <el-button type="primary" size="mini" icon="el-icon-upload2" @click="uploadPeriodTrack(currentPeriod.key)">选择</el-button>
                  <el-button type="success" size="mini" icon="el-icon-video-play" :disabled="!startTracks[currentPeriod.key]" @click="playPeriodTrack(currentPeriod.key)">播放</el-button>
                  <el-button v-if="startTracks[currentPeriod.key]" type="danger" size="mini" plain icon="el-icon-delete" @click="removePeriodTrack(currentPeriod.key)">删除</el-button>
                </div>
              </div>
              <el-form-item label="当前时段" class="pf-field">
                <el-select v-model="currentPeriodIndex" placeholder="请选择时段">
                  <el-option v-for="(period, index) in periods" :key="index" :label="period.label" :value="index"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="开始时间" class="pf-field">
                <el-time-picker v-model="periods[currentPeriodIndex].startTime" format="HH:mm" value-format="HH:mm" placeholder="HH:mm" class="time-input"></el-time-picker>
              </el-form-item>
              <el-form-item label="停止时间" class="pf-field">
                <el-time-picker v-model="periods[currentPeriodIndex].endTime" format="HH:mm" value-format="HH:mm" placeholder="HH:mm" class="time-input"></el-time-picker>
              </el-form-item>
              <el-form-item label="音乐文件" class="pf-field">
                <div class="file-control">
                  <template v-if="startTracks[currentPeriod.key]">
                    <i class="fas fa-file-audio"></i>
                    <span class="track-name-text" :title="startTracks[currentPeriod.key].name">{{ startTracks[currentPeriod.key].name }}</span>
                    <span class="track-sub">{{ formatDuration(startTracks[currentPeriod.key].duration) }} · {{ formatSize(startTracks[currentPeriod.key].size) }}</span>
                  </template>
                  <span v-else class="no-track">未选择（可点上方"选择"按钮上传）</span>
                </div>
              </el-form-item>
            </div>
          </el-form>

          <!-- 隐藏 audio 元素：src 由当前播放时段决定 -->
          <audio
            ref="startAudio"
            :src="currentStartSrc"
            @loadedmetadata="onStartMeta"
            @timeupdate="onStartTime"
            @ended="onStartEnded"
            @error="onAudioError"></audio>
        </el-card>

        <!-- 右侧：结束音乐播放列表（自定义） -->
        <el-card class="card end-card" shadow="hover">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="fas fa-list-ul"></i>
              <span>结束音乐播放列表</span>
              <el-tag size="mini" type="success" effect="plain">{{ endPlaylist.length }} 首</el-tag>
            </div>
            <el-button
              v-if="endPlaylist.length > 0"
              type="text"
              class="danger-text"
              icon="el-icon-delete"
              @click="clearPlaylist">清空</el-button>
          </div>

          <!-- 信息面板：播放结束时间 + 还需播放时间 + 列表总时长 -->
          <div class="end-info-panel">
            <div class="info-row">
              <span class="info-label"><i class="fas fa-flag-checkered"></i> 播放结束时间</span>
              <span class="info-value" :class="{ended: remainingSeconds <= 0}">
                {{ currentPeriod.endTime }}<span v-if="remainingSeconds <= 0">（已结束）</span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label"><i class="fas fa-hourglass-half"></i> 还需播放时间</span>
              <span class="info-value" :class="{urgent: remainingSeconds > 0 && remainingSeconds < 60, ended: remainingSeconds <= 0}">
                {{ formatRemaining(remainingSeconds) }}<span v-if="remainingSeconds > 0">（约 {{ Math.ceil(remainingSeconds / 60) }} 分钟）</span>
              </span>
            </div>
            <div class="info-row">
              <span class="info-label"><i class="fas fa-list-ol"></i> 列表总时长</span>
              <span class="info-value" :class="{over: endTotalDuration > remainingSeconds && remainingSeconds > 0}">
                {{ formatTime(endTotalDuration) }}<span v-if="endTotalDuration > 0 && remainingSeconds > 0">（{{ endTotalDuration > remainingSeconds ? '超出' : '尚余' }} {{ formatTime(Math.abs(endTotalDuration - remainingSeconds)) }}）</span>
              </span>
            </div>
          </div>

          <div v-if="library.length === 0" class="empty-start">
            <i class="fas fa-folder-open"></i>
            <p>乐曲库为空，请先在左上添加曲目</p>
          </div>

          <div v-else-if="endPlaylist.length === 0" class="empty-start">
            <i class="fas fa-circle-plus"></i>
            <p>播放列表为空</p>
            <span>从左上乐曲库点击 "+" 添加，或点下方"随机添加"</span>
          </div>

          <div v-else class="end-list">
            <p class="end-list-hint"><i class="fas fa-hand-pointer"></i> 长按列表音乐可拖动调整顺序</p>
            <div
              v-for="(t, i) in endPlaylist"
              :key="t.id + '_' + i"
              :class="['end-list-item', {
                active: i === endIndex,
                'drag-ready': dragReadyIndex === i,
                dragging: dragIndex === i,
                'drag-over': dragOverIndex === i && dragIndex !== -1 && dragIndex !== i
              }]"
              draggable="true"
              @click="onItemClick(i)"
              @mousedown="onItemMouseDown(i, $event)"
              @mouseup="onItemMouseUp"
              @mouseleave="onItemMouseLeave(i)"
              @dragstart="onDragStart(i, $event)"
              @dragover.prevent="onDragOver(i)"
              @dragleave="onDragLeave(i)"
              @drop.prevent="onDrop(i)"
              @dragend="onDragEnd">
              <span class="end-idx">{{ i + 1 }}</span>
              <div class="end-info">
                <div class="end-name" :title="t.name">{{ t.name }}</div>
                <div class="end-sub">{{ formatDuration(t.duration) }} · {{ formatSize(t.size) }}</div>
              </div>
              <el-button
                type="text"
                size="mini"
                class="end-play-btn"
                :icon="(i === endIndex && endPlaying) ? 'el-icon-video-pause' : 'el-icon-video-play'"
                @mousedown.stop
                @click.stop="onEndItemPlay(i)">
                {{ (i === endIndex && endPlaying) ? '暂停' : '播放' }}
              </el-button>
              <el-tag v-if="i === endIndex && endPlaying" size="mini" type="warning">
                <i class="fas fa-volume-high"></i> 播放中
              </el-tag>
              <el-tag v-else-if="i === endIndex" size="mini" type="info">已暂停</el-tag>
              <el-tag v-else size="mini" type="info" effect="plain">待播放</el-tag>
              <div class="end-order-btns" @mousedown.stop @click.stop>
                <button
                  type="button"
                  class="end-order-btn"
                  :disabled="i === 0"
                  :title="'上移'"
                  @click.stop="moveItem(i, -1)">
                  <i class="el-icon-caret-top"></i>
                </button>
                <button
                  type="button"
                  class="end-order-btn"
                  :disabled="i === endPlaylist.length - 1"
                  :title="'下移'"
                  @click.stop="moveItem(i, 1)">
                  <i class="el-icon-caret-bottom"></i>
                </button>
              </div>
              <el-button
                type="text"
                size="mini"
                class="danger-text end-remove-btn"
                icon="el-icon-close"
                @click.stop="removeFromPlaylist(i)"></el-button>
            </div>
            <audio
              ref="endAudio"
              @loadedmetadata="onEndMeta"
              @timeupdate="onEndTime"
              @ended="onEndEnded"
              @error="onAudioError"></audio>
          </div>

          <!-- 随机添加按钮（位于播放列表下方，蓝色，宽度与播放列表一致） -->
          <el-button
            class="end-random-add-btn"
            type="primary"
            icon="el-icon-magic-document"
            :disabled="library.length === 0"
            @click="addToPlaylistRandom">
            随机添加
          </el-button>
        </el-card>
        <!-- 左上：乐曲库 -->
        <el-card class="card library-card" shadow="hover">
          <div slot="header" class="card-header">
            <div class="card-title">
              <i class="fas fa-list-music"></i>
              <span>乐曲库</span>
              <el-tag size="mini" effect="plain">{{ library.length }} 首</el-tag>
            </div>
            <div>
              <el-button type="text" icon="el-icon-folder" @click="triggerFolderUpload">选择文件夹</el-button>
              <el-button type="text" icon="el-icon-upload2" @click="triggerUpload('end')">添加</el-button>
              <el-button
                v-if="library.length > 0"
                type="text"
                class="danger-text"
                icon="el-icon-delete"
                @click="clearLibrary">清空</el-button>
            </div>
          </div>

          <div v-if="library.length === 0" class="empty-library">
            <i class="fas fa-compact-disc"></i>
            <p>暂无曲目</p>
            <span>点击右上角"添加"按钮上传音乐文件</span>
            <el-button type="primary" size="small" icon="el-icon-upload2" @click="triggerUpload('end')">
              添加音乐
            </el-button>
          </div>

          <el-table
            v-else
            :data="library"
            border
            stripe
            size="mini"
            max-height="300"
            empty-text="乐曲库为空"
            class="library-table">
            <el-table-column type="index" label="#" width="32" align="center"></el-table-column>
            <el-table-column prop="name" label="曲目名称" min-width="160" show-overflow-tooltip>
              <template slot-scope="{ row }">
                <div class="row-name library-row-name">
                  <i class="fas fa-music"></i>
                  <span :title="row.name">{{ row.name }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="时长" width="60" align="center">
              <template slot-scope="{ row }">{{ formatDuration(row.duration) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="140" align="center">
              <template slot-scope="{ row }">
                <el-tooltip content="添加到结束音乐播放列表" placement="top">
                  <el-button type="text" size="mini" icon="el-icon-plus" @click="addToPlaylist(row)">
                    加入
                  </el-button>
                </el-tooltip>
                <el-tooltip content="试听" placement="top">
                  <el-button type="text" size="mini" icon="el-icon-video-play" @click="previewTrack(row)"></el-button>
                </el-tooltip>
                <el-tooltip content="删除" placement="top">
                  <el-button type="text" size="mini" class="danger-text" icon="el-icon-delete" @click="removeTrack(row)"></el-button>
                </el-tooltip>
              </template>
            </el-table-column>
          </el-table>
        </el-card>
    </div>

    <!-- 播放历史 -->
    <el-card class="card history-card" shadow="hover">
      <div slot="header" class="card-header">
        <div class="card-title">
          <i class="fas fa-clock-rotate-left"></i>
          <span>播放历史</span>
          <el-tag size="mini" effect="plain">最近 {{ history.length }} 条</el-tag>
        </div>
        <el-button
          v-if="history.length > 0"
          type="text"
          class="danger-text"
          icon="el-icon-delete"
          @click="clearHistory">清空</el-button>
      </div>
      <div v-if="history.length === 0" class="empty-history">
        <i class="fas fa-clock-rotate-left"></i>
        <span>暂无播放记录</span>
      </div>
      <el-timeline v-else>
        <el-timeline-item
          v-for="(item, idx) in history"
          :key="idx"
          :type="item.type === 'start' ? 'warning' : 'success'"
          :timestamp="item.playedAt" placement="top">
          <div class="history-row">
            <el-tag size="mini" :type="item.type === 'start' ? 'warning' : 'success'" effect="plain">
              {{ item.type === 'start' ? '开始' : '结束' }}
            </el-tag>
            <el-tag v-if="item.periodKey" size="mini" :type="periodTagType(item.periodKey)" effect="plain">
              {{ item.periodLabel }}
            </el-tag>
            <span class="history-name" :title="item.name">{{ item.name }}</span>
          </div>
        </el-timeline-item>
      </el-timeline>
    </el-card>

    <!-- 隐藏的文件选择器 -->
    <input
      ref="fileInput"
      type="file"
      accept="audio/*"
      multiple
      style="display:none"
      @change="onFileChange">
    <!-- 隐藏的文件夹选择器（webkitdirectory 需在 mounted 中通过 setAttribute 设置） -->
    <input
      ref="folderInput"
      type="file"
      multiple
      style="display:none"
      @change="onFolderChange">

    <!-- 隐藏的试听 audio 元素（供底部播放条统一接管） -->
    <audio
      ref="previewAudio"
      @ended="onPreviewEnded"
      @error="onAudioError"></audio>

    <!-- ===== 底部固定播放控制条 ===== -->
    <transition name="slide-up">
      <div v-if="hasActive" class="player-bar">
        <div class="bar-left">
          <el-tag size="mini" :type="activeTypeTag" effect="dark">{{ activeTypeLabel }}</el-tag>
          <div class="bar-track-name" :title="activeContext.name">
            <i class="fas fa-music"></i>
            <span>{{ activeContext.name }}</span>
          </div>
        </div>
        <div class="bar-center">
          <el-button-group>
            <el-button
              size="mini"
              :type="activeContext.isPlaying ? 'warning' : 'primary'"
              :icon="activeContext.isPlaying ? 'el-icon-video-pause' : 'el-icon-video-play'"
              @click="toggleActive">
              {{ activeContext.isPlaying ? '暂停' : '播放' }}
            </el-button>
            <el-button size="mini" icon="el-icon-close" @click="stopActive">停止</el-button>
          </el-button-group>
          <span class="bar-time">{{ formatTime(activeContext.current) }}</span>
          <div class="bar-progress-wrap" @mousedown="startBarSeek">
            <el-slider
              v-model="activeContext.current"
              :max="activeContext.duration || 0"
              :step="0.1"
              :show-tooltip="false"
              :disabled="!activeContext.duration"
              class="bar-progress"
              @change="onBarSliderChange"></el-slider>
          </div>
          <span class="bar-time">{{ formatTime(activeContext.duration) }}</span>
        </div>
        <div class="bar-right">
          <i class="fas fa-volume-up"></i>
          <el-slider v-model="volume" :min="0" :max="100" :show-tooltip="false" class="bar-volume"></el-slider>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
// IndexedDB 封装：持久化音频 Blob，刷新页面后仍可播放
const DB_NAME = 'MusicDB';
const DB_VERSION = 1;
const STORE_NAME = 'tracks';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = e => resolve(e.target.result);
    req.onerror = e => reject(e.target.error);
  });
}

function dbPut(track) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(track);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

function dbGet(id) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

function dbDelete(id) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

function dbClear() {
  return openDB().then(db => new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }));
}

// localStorage 键
const LS_META = 'music_library_meta';      // 结束音乐库元数据列表
const LS_START = 'music_start_meta';        // 开始音乐元数据
const LS_HISTORY = 'music_history';         // 播放历史
const LS_FADE = 'music_fade_settings';      // 淡入淡出设置 {enabled, duration}
const LS_PERIODS = 'music_periods_config';  // 时段配置 {startTime, endTime}
const LS_MANUAL_PERIOD = 'music_manual_period';  // 手动选择的时段索引（null=按时间自动）

function readLS(key, def) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return def;
    const v = JSON.parse(raw);
    return v === undefined ? def : v;
  } catch { return def; }
}
function writeLS(key, val) {
  localStorage.setItem(key, JSON.stringify(val));
}

export default {
  name: 'MusicControl',
  data() {
    return {
      uploadType: 'end',           // 'start' 或 'end'
      uploadStartKey: null,        // 上传开始音乐时指定的时段 key
      volume: 80,
      // 时段配置（早/中/下午的开始、停止时间）
      periods: [
        { key: 'morning',   label: '早上', startTime: '08:10', endTime: '08:30' },
        { key: 'noon',      label: '中午', startTime: '11:45', endTime: '12:15' },
        { key: 'afternoon', label: '下午', startTime: '17:30', endTime: '18:00' }
      ],
      // 三段开始音乐：每个时段的元数据 {id,name,size,duration,addedAt}
      startTracks: { morning: null, noon: null, afternoon: null },
      // 三段开始音乐的 ObjectURL
      startSrcs: { morning: null, noon: null, afternoon: null },
      activeStartKey: null,       // 当前播放/选中的时段 key
      startPlaying: false,
      startCurrent: 0,
      startDuration: 0,
      // 结束音乐：根据剩余时间抽取的多首曲目列表（按顺序播放）
      endPlaylist: [],         // 数组：[{id,name,size,duration,addedAt}]
      endPlaylistSrcs: [],                  // 与 endPlaylist 同步的 ObjectURL 列表
      endIndex: -1,                 // 当前播放索引，-1 表示未开始
      endPlaying: false,
      endCurrent: 0,
      endDuration: 0,
      // 试听
      previewSrc: null,
      // 试听曲目元数据（用于底部播放条显示）
      previewMeta: null,
      // 全局播放上下文：当前正在播放的音乐（用于底部统一控制条）
      activeContext: {
        type: null,        // 'start' | 'end' | 'preview'
        name: '无',
        source: null,     // 当前播放的 audio 元素引用
        current: 0,
        duration: 0,
        isPlaying: false
      },
      syncRaf: null,      // rAF 句柄，用于同步 audio 状态到 activeContext
      // 乐曲库（结束音乐备选元数据列表）
      library: [],
      // 播放历史
      history: [],
      // 自动调度（到点自动播放/停止）
      schedulerEnabled: true,
      schedulerTimer: null,
      lastTriggerKey: null,        // 避免同一分钟重复触发，格式：'start_morning' / 'end_noon'
      // 当前时间（每秒刷新，用于展示和时段判断）
      nowTime: new Date(),
      // 淡入淡出（纯前端实现：定时器调整 audio.volume）
      fadeEnabled: true,
      fadeDuration: 2,             // 秒数
      // 手动选择的时段索引（null=按当前时间自动），持久化到 LS
      manualPeriodIndex: null,
      // 底部进度条拖拽互斥锁：true 时 rAF 不覆盖 activeContext.current
      isBarSeeking: false,
      // 结束音乐播放列表拖拽排序（长按触发）
      longPressTimer: null,    // 长按定时器句柄
      dragReadyIndex: -1,      // 长按已激活、可拖拽的索引
      dragIndex: -1,           // 正在拖拽的源索引
      dragOverIndex: -1        // 拖拽悬停的目标索引
    };
  },
  computed: {
    // 当前时段：优先使用 manualPeriodIndex（手动选择），否则按 currentPeriodIndex 推导
    currentPeriod() {
      return this.periods[this.currentPeriodIndex];
    },
    // 当前时段在 periods 数组中的索引（带 getter/setter 以支持 v-model 和持久化）
    currentPeriodIndex: {
      get() {
        if (this.manualPeriodIndex !== null && this.manualPeriodIndex >= 0 && this.manualPeriodIndex < this.periods.length) {
          return this.manualPeriodIndex;
        }
        const m = this.nowTime.getHours() * 60 + this.nowTime.getMinutes();
        if (m < 11 * 60 + 45) return 0;
        if (m < 17 * 60 + 30) return 1;
        return 2;
      },
      set(v) {
        const idx = Number(v);
        if (!isNaN(idx) && idx >= 0 && idx < this.periods.length) {
          this.manualPeriodIndex = idx;
        } else {
          this.manualPeriodIndex = null;
        }
        writeLS(LS_MANUAL_PERIOD, this.manualPeriodIndex);
      }
    },
    // 当前播放/选中的开始音乐元数据
    currentStartTrack() {
      const key = this.activeStartKey || this.currentPeriod.key;
      return this.startTracks[key];
    },
    // 当前播放/选中的开始音乐 src
    currentStartSrc() {
      const key = this.activeStartKey || this.currentPeriod.key;
      return this.startSrcs[key];
    },
    // 当前播放音乐名称（用于信息面板展示）
    currentPlayingName() {
      if (!this.startPlaying || !this.activeStartKey) return '无';
      const t = this.startTracks[this.activeStartKey];
      return t ? t.name : '无';
    },
    // 当前时间 HH:mm:ss
    nowHHmmss() {
      const p = n => String(n).padStart(2, '0');
      const d = this.nowTime;
      return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
    // 底部播放条：是否有活跃播放源
    hasActive() {
      return !!this.activeContext.source;
    },
    // 底部播放条：活跃类型标签
    activeTypeLabel() {
      const map = { start: '开始音乐', end: '结束音乐', preview: '试听' };
      return map[this.activeContext.type] || '—';
    },
    // 底部播放条：活跃类型对应的 Tag 颜色
    activeTypeTag() {
      const map = { start: 'warning', end: 'success', preview: 'info' };
      return map[this.activeContext.type] || '';
    },
    // 当前时段结束时间的剩余秒数（动态：随 nowTime 每秒刷新）
    remainingSeconds() {
      const p = this.currentPeriod;
      const [eh, em] = p.endTime.split(':').map(Number);
      const now = this.nowTime;
      const end = new Date(now);
      end.setHours(eh, em, 0, 0);
      const diff = (end - now) / 1000;
      return diff > 0 ? diff : 0;
    },
    // 已抽取结束音乐的总时长（秒）
    endTotalDuration() {
      return this.endPlaylist.reduce((s, t) => s + (t.duration || 0), 0);
    }
  },
  async mounted() {
    // 加载元数据
    this.library = readLS(LS_META, []);
    this.history = readLS(LS_HISTORY, []);
    // 加载手动选择的时段（null 或 0/1/2），刷新后保持用户选择
    const savedManualPeriod = readLS(LS_MANUAL_PERIOD, null);
    if (savedManualPeriod === null || (typeof savedManualPeriod === 'number' && savedManualPeriod >= 0 && savedManualPeriod < this.periods.length)) {
      this.manualPeriodIndex = savedManualPeriod;
    }
    // 加载淡入淡出设置
    const fade = readLS(LS_FADE, null);
    if (fade && typeof fade.enabled === 'boolean') this.fadeEnabled = fade.enabled;
    if (fade && typeof fade.duration === 'number') this.fadeDuration = fade.duration;
    // 加载时段配置（startTime/endTime），覆盖默认值
    const savedPeriods = readLS(LS_PERIODS, null);
    if (Array.isArray(savedPeriods) && savedPeriods.length === this.periods.length) {
      this.periods.forEach((p, i) => {
        const s = savedPeriods[i] || {};
        if (s.startTime) this.$set(p, 'startTime', s.startTime);
        if (s.endTime) this.$set(p, 'endTime', s.endTime);
      });
    }
    // 恢复三段开始音乐
    const startMetaMap = readLS(LS_START, {});
    for (const p of this.periods) {
      const meta = startMetaMap[p.key];
      if (meta && meta.id) {
        const blob = await dbGet(meta.id);
        if (blob) {
          this.$set(this.startTracks, p.key, meta);
          this.$set(this.startSrcs, p.key, URL.createObjectURL(blob.blob));
        }
      }
    }
    // 设置文件夹选择器属性（Vue 2 不会自动渲染 webkitdirectory）
    this.$nextTick(() => {
      if (this.$refs.folderInput) {
        this.$refs.folderInput.setAttribute('webkitdirectory', '');
        this.$refs.folderInput.setAttribute('directory', '');
      }
    });
    // 启动时钟（每秒刷新，同时驱动调度检查）
    this.schedulerTimer = setInterval(() => {
      this.nowTime = new Date();
      if (this.schedulerEnabled) this.checkSchedule();
    }, 1000);
  },
  beforeDestroy() {
    if (this.schedulerTimer) {
      clearInterval(this.schedulerTimer);
      this.schedulerTimer = null;
    }
    if (this.syncRaf) {
      cancelAnimationFrame(this.syncRaf);
      this.syncRaf = null;
    }
    if (this._barSeekDocOff) { this._barSeekDocOff(); }
    if (this._barSeekTimer) { clearTimeout(this._barSeekTimer); this._barSeekTimer = null; }
    this.isBarSeeking = false;
    // 清理长按定时器
    this.clearLongPressTimer();
    // 释放 ObjectURL 避免内存泄漏
    Object.values(this.startSrcs).forEach(u => u && URL.revokeObjectURL(u));
    this.endPlaylistSrcs.forEach(u => u && URL.revokeObjectURL(u));
    if (this.previewSrc) URL.revokeObjectURL(this.previewSrc);
  },
  watch: {
    volume(v) {
      if (this.$refs.startAudio) this.$refs.startAudio.volume = v / 100;
      if (this.$refs.endAudio) this.$refs.endAudio.volume = v / 100;
    },
    // 时段配置（时间）深度持久化
    periods: {
      handler(list) {
        writeLS(LS_PERIODS, list.map(p => ({
          key: p.key, startTime: p.startTime, endTime: p.endTime
        })));
      },
      deep: true
    },
    // 切换播放时段时，重置进度
    activeStartKey() {
      this.startCurrent = 0;
      this.startDuration = 0;
    },
    // 切换时段时，清空结束音乐播放列表（避免与上一个时段混淆）
    currentPeriodIndex(newVal, oldVal) {
      if (newVal === oldVal) return;
      // 停止正在播放的结束音乐
      if (this.endPlaying || this.endIndex >= 0) {
        this.stopEnd(true).catch(() => {});
      }
      // 释放 ObjectURL
      this.endPlaylistSrcs.forEach(u => u && URL.revokeObjectURL(u));
      this.endPlaylistSrcs = [];
      this.endPlaylist = [];
      this.endIndex = -1;
    },
    // 持久化淡入淡出设置
    fadeEnabled() { this.saveFade(); },
    fadeDuration() { this.saveFade(); }
  },
  methods: {
    // ===== 文件上传 =====
    triggerUpload(type) {
      this.uploadType = type;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    // 选择文件夹：扫描目录下所有音频文件批量入库
    triggerFolderUpload() {
      this.$refs.folderInput.value = '';
      this.$refs.folderInput.click();
    },
    async onFolderChange(e) {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      // 筛选音频文件（按扩展名）
      const audioExts = ['.mp3', '.wav', '.ogg', '.oga', '.m4a', '.aac', '.flac', '.weba', '.opus', '.wma'];
      const audioFiles = files.filter(f => {
        const name = f.name.toLowerCase();
        return audioExts.some(ext => name.endsWith(ext));
      });
      if (audioFiles.length === 0) {
        this.$message.warning(`未在所选文件夹中找到音频文件（共扫描 ${files.length} 个文件）`);
        return;
      }
      // 去重：跳过乐曲库中已存在同名且同大小的曲目
      const existing = new Set(this.library.map(t => `${t.name}_${t.size}`));
      const toAdd = audioFiles.filter(f => !existing.has(`${f.name}_${f.size}`));
      const skipped = audioFiles.length - toAdd.length;
      for (const f of toAdd) {
        await this.addEndTrack(f);
      }
      let msg = `已从文件夹添加 ${toAdd.length} 首曲目`;
      if (skipped > 0) msg += `（跳过 ${skipped} 首已存在）`;
      msg += `（共扫描 ${files.length} 个文件）`;
      this.$message.success(msg);
    },
    async onFileChange(e) {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      if (this.uploadType === 'start') {
        // 开始音乐：上传到指定时段
        if (!this.uploadStartKey) {
          this.$message.warning('时段未指定');
          return;
        }
        await this.addPeriodTrack(files[0], this.uploadStartKey);
        this.uploadStartKey = null;
      } else {
        // 结束音乐支持多选批量添加
        for (const f of files) {
          await this.addEndTrack(f);
        }
        this.$message.success(`已添加 ${files.length} 首曲目`);
      }
    },
    // 触发某时段的开始音乐上传
    uploadPeriodTrack(key) {
      this.uploadType = 'start';
      this.uploadStartKey = key;
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    },
    async addPeriodTrack(file, key) {
      const id = `start_${key}_${Date.now()}`;
      const meta = {
        id,
        name: file.name,
        size: file.size,
        duration: await this.getAudioDuration(file),
        addedAt: this.formatDateTime(new Date())
      };
      await dbPut({ id, blob: file });
      // 清理该时段旧文件
      const old = this.startTracks[key];
      if (old && old.id !== id) {
        await dbDelete(old.id).catch(() => {});
      }
      if (this.startSrcs[key]) URL.revokeObjectURL(this.startSrcs[key]);
      this.$set(this.startTracks, key, meta);
      this.$set(this.startSrcs, key, URL.createObjectURL(file));
      // 切换到该时段，便于立即播放
      this.activeStartKey = key;
      this.startCurrent = 0;
      this.startDuration = 0;
      this.startPlaying = false;
      this.saveStartMeta();
      const p = this.periods.find(x => x.key === key);
      this.$message.success(`${p ? p.label : ''}时段开始音乐已设置`);
    },
    async removePeriodTrack(key) {
      const t = this.startTracks[key];
      if (!t) return;
      try {
        await this.$confirm(`确认删除${this.periods.find(x => x.key === key).label}时段的开始音乐？`, '提示', {
          type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消'
        });
      } catch { return; }
      await dbDelete(t.id).catch(() => {});
      if (this.startSrcs[key]) URL.revokeObjectURL(this.startSrcs[key]);
      this.$set(this.startTracks, key, null);
      this.$set(this.startSrcs, key, null);
      // 如果删的是当前播放时段，停止播放
      if (this.activeStartKey === key) {
        this.stopStart();
        this.activeStartKey = null;
      }
      this.saveStartMeta();
      this.$message.success('已删除');
    },
    // 持久化三段开始音乐元数据
    saveStartMeta() {
      const map = {};
      for (const p of this.periods) {
        if (this.startTracks[p.key]) map[p.key] = this.startTracks[p.key];
      }
      writeLS(LS_START, map);
    },
    saveFade() {
      writeLS(LS_FADE, { enabled: this.fadeEnabled, duration: this.fadeDuration });
    },
    // ===== 淡入淡出（定时器调整 audio.volume） =====
    // 淡入：从 0 渐变到目标音量；返回 Promise 完成时音量已达目标
    fadeIn(audioEl) {
      return new Promise(resolve => {
        if (!audioEl) return resolve();
        this.clearFades(audioEl);
        // 未开启淡入淡出或时长无效：直接设为目标音量
        const target = this.volume / 100;
        if (!this.fadeEnabled || !(this.fadeDuration > 0)) {
          audioEl.volume = target;
          return resolve();
        }
        const steps = Math.max(1, Math.floor(this.fadeDuration * 1000 / 50));
        let i = 0;
        audioEl.volume = 0;
        const timer = setInterval(() => {
          i++;
          if (i >= steps) {
            audioEl.volume = target;
            clearInterval(timer);
            resolve();
          } else {
            audioEl.volume = target * (i / steps);
          }
        }, 50);
        audioEl._fadeTimer = timer;
      });
    },
    // 淡出：从当前音量渐变到 0；完成后 resolve
    fadeOut(audioEl) {
      return new Promise(resolve => {
        if (!audioEl) return resolve();
        this.clearFades(audioEl);
        if (!this.fadeEnabled || !(this.fadeDuration > 0)) {
          audioEl.volume = 0;
          return resolve();
        }
        const startVol = audioEl.volume || 0;
        if (startVol <= 0) return resolve();
        const steps = Math.max(1, Math.floor(this.fadeDuration * 1000 / 50));
        let i = 0;
        const timer = setInterval(() => {
          i++;
          if (i >= steps) {
            audioEl.volume = 0;
            clearInterval(timer);
            resolve();
          } else {
            audioEl.volume = startVol * (1 - i / steps);
          }
        }, 50);
        audioEl._fadeTimer = timer;
      });
    },
    clearFades(audioEl) {
      if (audioEl && audioEl._fadeTimer) {
        clearInterval(audioEl._fadeTimer);
        audioEl._fadeTimer = null;
      }
    },
    // ===== 全局播放上下文：供底部固定播放条统一控制 =====
    // 设置当前活跃播放源；所有播放入口在成功 play() 后调用
    setActivePlayer(type, name, audioEl) {
      this.activeContext.type = type;
      this.activeContext.name = name || '无';
      this.activeContext.source = audioEl || null;
      this.activeContext.isPlaying = !!audioEl && !audioEl.paused;
      this.activeContext.duration = (audioEl && audioEl.duration) || 0;
      this.activeContext.current = (audioEl && audioEl.currentTime) || 0;
      this.ensureSyncLoop();
    },
    // 清除活跃播放源（仅在 source 与传入元素匹配时清除，避免误清）
    clearActivePlayer(audioEl) {
      if (audioEl && this.activeContext.source !== audioEl) return;
      this.activeContext.source = null;
      this.activeContext.type = null;
      this.activeContext.name = '无';
      this.activeContext.isPlaying = false;
      this.activeContext.current = 0;
      this.activeContext.duration = 0;
      // 兜底：切歌/停播时立即释放进度条拖拽锁，防止悬挂导致后续播放不推进
      this.isBarSeeking = false;
    },
    // rAF 同步循环：每帧把 audio 状态同步到 activeContext
    ensureSyncLoop() {
      if (this.syncRaf) return;
      const step = () => {
        const a = this.activeContext.source;
        if (a) {
          // source 可能已被销毁/置空（试听结束后）
          try {
            // 拖拽底部进度条时不覆盖 current，避免滑块回弹
            if (!this.isBarSeeking) {
              this.activeContext.current = a.currentTime || 0;
            }
            this.activeContext.duration = a.duration || 0;
            this.activeContext.isPlaying = !a.paused && !a.ended;
          } catch (e) { /* 忽略已释放的引用 */ }
          // 若 source 已停止播放且 ended，自动清除
          if (a.ended) {
            // 不强制清除（自然结束后保留信息），仅标记 isPlaying
            this.activeContext.isPlaying = false;
          }
        }
        // 持续刷新直到 source 为空
        if (this.activeContext.source) {
          this.syncRaf = requestAnimationFrame(step);
        } else {
          this.syncRaf = null;
        }
      };
      this.syncRaf = requestAnimationFrame(step);
    },
    // ===== 底部播放条操作 =====
    async toggleActive() {
      const a = this.activeContext.source;
      if (!a) return;
      const t = this.activeContext.type;
      if (t === 'start') {
        await this.toggleStart();
      } else if (t === 'end') {
        await this.toggleEnd();
      } else if (t === 'preview') {
        // 试听直接操作 previewAudio
        if (!a.paused) {
          a.pause();
          this.activeContext.isPlaying = false;
        } else {
          try {
            await a.play();
            this.activeContext.isPlaying = true;
          } catch { this.$message.warning('播放失败'); }
        }
      }
    },
    async stopActive() {
      const t = this.activeContext.type;
      if (t === 'start') {
        await this.stopStart();
      } else if (t === 'end') {
        await this.stopEnd();
      } else if (t === 'preview') {
        // 试听停止：先淡出再停
        const a = this.$refs.previewAudio;
        if (a) {
          if (this.activeContext.isPlaying) await this.fadeOut(a);
          this.clearFades(a);
          a.pause();
          a.currentTime = 0;
          a.volume = this.volume / 100;
        }
        this.clearActivePlayer(a);
        if (this.previewSrc) {
          URL.revokeObjectURL(this.previewSrc);
          this.previewSrc = null;
        }
        this.previewMeta = null;
      }
    },
    // 底部进度条：用户鼠标按下进度条区域 → 上锁 + 注册 document 级监听器（兼容在容器外松手/失焦）
    startBarSeek() {
      this.isBarSeeking = true;
      if (this._barSeekDocOff) this._barSeekDocOff(); // 先清掉旧的
      const handler = () => off();
      const off = () => {
        document.removeEventListener('mouseup', handler, true);
        document.removeEventListener('blur', handler, true);
        this._barSeekDocOff = null;
        this.endBarSeek();
      };
      document.addEventListener('mouseup', handler, true);
      document.addEventListener('blur', handler, true);
      this._barSeekDocOff = off;
    },
    // 底部进度条：结束交互 → 执行真实 seek，延迟 30ms 解锁（等 audio seek 同步完，避免回弹）
    endBarSeek() {
      if (this._barSeekTimer) clearTimeout(this._barSeekTimer);
      this._barSeekTimer = setTimeout(() => {
        this.isBarSeeking = false;
        this._barSeekTimer = null;
      }, 30);
    },
    // 底部进度条：用户松手/点击完成 → 跳转 + 结束
    onBarSliderChange(v) {
      this.seekActive(v);
      this.endBarSeek();
    },
    seekActive(v) {
      const a = this.activeContext.source;
      if (a) a.currentTime = v;
    },
    async addEndTrack(file) {
      const id = 'end_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6);
      const meta = {
        id,
        name: file.name,
        size: file.size,
        duration: await this.getAudioDuration(file),
        addedAt: this.formatDateTime(new Date())
      };
      await dbPut({ id, blob: file });
      this.library.push(meta);
      writeLS(LS_META, this.library);
    },
    // 读取音频时长
    getAudioDuration(file) {
      return new Promise(resolve => {
        const url = URL.createObjectURL(file);
        const a = new Audio(url);
        a.onloadedmetadata = () => {
          const d = a.duration;
          URL.revokeObjectURL(url);
          resolve(isFinite(d) ? d : 0);
        };
        a.onerror = () => {
          URL.revokeObjectURL(url);
          resolve(0);
        };
      });
    },

    // ===== 开始音乐控制 =====
    // 播放指定时段的开始音乐（切换 activeStartKey → 重新加载 → 播放）
    async playPeriodTrack(key) {
      if (!this.startTracks[key]) {
        this.$message.warning('该时段尚未设置音乐');
        return;
      }
      this.activeStartKey = key;
      await this.$nextTick();
      const a = this.$refs.startAudio;
      if (!a) return;
      // 同时只播放一首：互斥立即停止结束音乐
      await this.stopEnd(true);
      try {
        a.currentTime = 0;
        await a.play();
        this.startPlaying = true;
        this.fadeIn(a);
        this.recordHistory('start', this.startTracks[key].name);
        this.setActivePlayer('start', this.startTracks[key].name, a);
      } catch {
        this.$message.warning('播放失败，请检查文件格式');
      }
    },
    async toggleStart() {
      const a = this.$refs.startAudio;
      if (!a || !this.currentStartTrack) return;
      if (this.startPlaying) {
        a.pause();
        this.startPlaying = false;
        this.activeContext.isPlaying = false;
      } else {
        await this.stopEnd(true);
        try {
          a.currentTime = 0;
          await a.play();
          this.startPlaying = true;
          this.fadeIn(a);
          if (!this.activeStartKey) this.activeStartKey = this.currentPeriod.key;
          this.recordHistory('start', this.startTracks[this.activeStartKey].name);
          this.setActivePlayer('start', this.startTracks[this.activeStartKey].name, a);
        } catch {
          this.$message.warning('播放失败，请检查文件格式');
        }
      }
    },
    // 停止开始音乐；immediate=true 时跳过淡出（用于互斥切换）
    async stopStart(immediate = false) {
      const a = this.$refs.startAudio;
      if (a) {
        if (this.startPlaying && !immediate) {
          await this.fadeOut(a);
        }
        this.clearFades(a);
        a.pause();
        a.currentTime = 0;
        a.volume = this.volume / 100;
      }
      this.startPlaying = false;
      this.startCurrent = 0;
      this.clearActivePlayer(a);
    },
    onStartMeta() {
      this.startDuration = this.$refs.startAudio.duration || 0;
      this.$refs.startAudio.volume = this.volume / 100;
    },
    onStartTime() {
      if (this.startPlaying) this.startCurrent = this.$refs.startAudio.currentTime;
    },
    onStartSeek(v) {
      if (this.$refs.startAudio) this.$refs.startAudio.currentTime = v;
    },
    onStartEnded() {
      this.startPlaying = false;
      this.startCurrent = 0;
      this.clearActivePlayer(this.$refs.startAudio);
    },

    // ===== 自动调度：到点自动播放/停止 =====
    onSchedulerToggle(val) {
      if (val) {
        this.lastTriggerKey = null;  // 重新开启时清除触发记忆，确保下次能触发
        this.$message.success('已开启自动调度');
      } else {
        this.$message.info('已关闭自动调度');
      }
    },
    checkSchedule() {
      const now = this.nowTime;
      const p = n => String(n).padStart(2, '0');
      const hhmm = `${p(now.getHours())}:${p(now.getMinutes())}`;
      for (const period of this.periods) {
        // 到达开始时间 → 自动播放该时段音乐
        if (hhmm === period.startTime) {
          const trigKey = `start_${period.key}`;
          if (this.lastTriggerKey !== trigKey) {
            this.lastTriggerKey = trigKey;
            this.autoPlayPeriod(period);
          }
        }
        // 到达结束时间 → 自动停止当前正在播放的音乐
        if (hhmm === period.endTime) {
          const trigKey = `end_${period.key}`;
          if (this.lastTriggerKey !== trigKey) {
            this.lastTriggerKey = trigKey;
            this.autoStopAll(period);
          }
        }
      }
    },
    // 自动播放某时段的开始音乐
    async autoPlayPeriod(period) {
      if (!this.startTracks[period.key]) {
        this.$notify({
          title: '自动播放失败',
          message: `${period.label}时段（${period.startTime}）尚未设置开始音乐`,
          type: 'warning',
          duration: 6000
        });
        return;
      }
      // 互斥立即停止结束音乐
      await this.stopEnd(true);
      this.activeStartKey = period.key;
      await this.$nextTick();
      const a = this.$refs.startAudio;
      if (!a) return;
      try {
        a.currentTime = 0;
        await a.play();
        this.startPlaying = true;
        this.fadeIn(a);
        this.recordHistory('start', this.startTracks[period.key].name);
        this.setActivePlayer('start', this.startTracks[period.key].name, a);
        this.$notify({
          title: '已自动播放',
          message: `${period.label} · ${this.startTracks[period.key].name}`,
          type: 'success',
          duration: 4000
        });
      } catch {
        this.$notify({
          title: '自动播放失败',
          message: '音频播放被浏览器拦截，请手动点击播放',
          type: 'error',
          duration: 6000
        });
      }
    },
    // 到结束时间自动停止所有正在播放的音乐（淡出后停止）
    async autoStopAll(period) {
      const wasPlaying = this.startPlaying || this.endPlaying;
      // 同时淡出开始/结束音乐
      await Promise.all([this.stopStart(), this.stopEnd()]);
      if (wasPlaying) {
        this.$notify({
          title: '已自动停止',
          message: `${period.label}时段结束（${period.endTime}）`,
          type: 'info',
          duration: 4000
        });
      }
    },

    // ===== 结束音乐播放列表（自定义） =====
    // 从乐曲库点击 "+" 添加单首到播放列表末尾
    async addToPlaylist(row) {
      if (!row) return;
      // 避免重复添加同一首
      if (this.endPlaylist.some(t => t.id === row.id)) {
        this.$message.info(`「${row.name}」已在播放列表中`);
        return;
      }
      const blob = await dbGet(row.id);
      if (!blob) {
        this.$message.error('文件读取失败，请重新添加到乐曲库');
        return;
      }
      this.endPlaylist.push({ ...row });
      this.endPlaylistSrcs.push(URL.createObjectURL(blob.blob));
      this.$message.success(`已添加到播放列表：${row.name}`);
    },
    // 随机从乐曲库抽一首添加到播放列表末尾
    async addToPlaylistRandom() {
      if (this.library.length === 0) {
        this.$message.warning('乐曲库为空');
        return;
      }
      // 优先选未在播放列表中的，避免重复
      const inListIds = new Set(this.endPlaylist.map(t => t.id));
      const candidates = this.library.filter(t => !inListIds.has(t.id));
      const pool = candidates.length > 0 ? candidates : this.library;
      const pick = pool[Math.floor(Math.random() * pool.length)];
      await this.addToPlaylist(pick);
    },
    // 清空整个播放列表
    async clearPlaylist() {
      if (this.endPlaylist.length === 0) return;
      try {
        await this.$confirm('确认清空播放列表？', '提示', {
          type: 'warning', confirmButtonText: '清空', cancelButtonText: '取消'
        });
      } catch { return; }
      await this.stopEnd(true);
      this.endPlaylistSrcs.forEach(u => u && URL.revokeObjectURL(u));
      this.endPlaylistSrcs = [];
      this.endPlaylist = [];
      this.endIndex = -1;
      this.$message.success('已清空播放列表');
    },
    // 从播放列表移除指定索引的曲目
    async removeFromPlaylist(idx) {
      if (idx < 0 || idx >= this.endPlaylist.length) return;
      const t = this.endPlaylist[idx];
      try {
        await this.$confirm(`从播放列表移除「${t.name}」？`, '提示', {
          type: 'warning', confirmButtonText: '移除', cancelButtonText: '取消'
        });
      } catch { return; }
      // 如果正在播放该索引，先停止
      if (this.endIndex === idx) {
        await this.stopEnd(true);
      }
      // 释放该 src
      if (this.endPlaylistSrcs[idx]) URL.revokeObjectURL(this.endPlaylistSrcs[idx]);
      this.endPlaylist.splice(idx, 1);
      this.endPlaylistSrcs.splice(idx, 1);
      // 调整 endIndex
      if (this.endIndex > idx) {
        this.endIndex -= 1;
      } else if (this.endIndex === idx) {
        this.endIndex = -1;
      }
      this.$message.success('已移除');
    },
    // ===== 播放列表项顺序调整（上下三角按钮 + 长按拖拽） =====
    // 通过三角按钮上移/下移；dir = -1 上移，+1 下移
    moveItem(idx, dir) {
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= this.endPlaylist.length) return;
      // 同步交换 endPlaylist 与 endPlaylistSrcs
      const track = this.endPlaylist[idx];
      const src = this.endPlaylistSrcs[idx];
      this.endPlaylist.splice(idx, 1);
      this.endPlaylistSrcs.splice(idx, 1);
      this.endPlaylist.splice(newIdx, 0, track);
      this.endPlaylistSrcs.splice(newIdx, 0, src);
      // 同步调整当前播放索引，保证播放不被中断（除非移动了正在播放项）
      if (this.endIndex === idx) {
        this.endIndex = newIdx;
      } else if (this.endIndex === newIdx) {
        // 被挤开的项整体反向移动一格
        this.endIndex = idx;
      }
    },
    // 列表项点击：长按激活后忽略本次点击，避免触发播放
    onItemClick(i) {
      if (this.dragReadyIndex === i) {
        this.dragReadyIndex = -1;
        return;
      }
      this.playEndAt(i);
    },
    // 鼠标按下：启动长按定时器（500ms）
    onItemMouseDown(i, e) {
      // 仅响应主键（左键）
      if (e.button !== 0) return;
      this.clearLongPressTimer();
      this.longPressTimer = setTimeout(() => {
        this.dragReadyIndex = i;
      }, 500);
    },
    // 鼠标抬起：清除定时器
    onItemMouseUp() {
      this.clearLongPressTimer();
    },
    // 鼠标离开：清除定时器（但不影响已在拖拽中的状态）
    onItemMouseLeave(i) {
      this.clearLongPressTimer();
    },
    clearLongPressTimer() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },
    // 拖拽开始：未长按激活则取消，否则记录源索引
    onDragStart(i, e) {
      if (this.dragReadyIndex !== i) {
        e.preventDefault();
        return;
      }
      this.dragIndex = i;
      // 设置 dataTransfer 以兼容某些浏览器要求
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        try { e.dataTransfer.setData('text/plain', String(i)); } catch (_) {}
      }
    },
    // 拖拽悬停：记录目标索引
    onDragOver(i) {
      if (this.dragIndex === -1) return;
      this.dragOverIndex = i;
    },
    // 拖拽离开：清除悬停标记
    onDragLeave(i) {
      if (this.dragOverIndex === i) this.dragOverIndex = -1;
    },
    // 释放：执行重排序
    onDrop(i) {
      const from = this.dragIndex;
      if (from === -1 || from === i) {
        this.resetDragState();
        return;
      }
      // 同步移动 endPlaylist 与 endPlaylistSrcs
      const track = this.endPlaylist[from];
      const src = this.endPlaylistSrcs[from];
      this.endPlaylist.splice(from, 1);
      this.endPlaylistSrcs.splice(from, 1);
      this.endPlaylist.splice(i, 0, track);
      this.endPlaylistSrcs.splice(i, 0, src);
      // 调整当前播放索引
      if (this.endIndex === from) {
        this.endIndex = i;
      } else if (from < this.endIndex && i >= this.endIndex) {
        this.endIndex -= 1;
      } else if (from > this.endIndex && i <= this.endIndex) {
        this.endIndex += 1;
      }
      this.resetDragState();
    },
    // 拖拽结束：重置所有状态
    onDragEnd() {
      this.resetDragState();
    },
    resetDragState() {
      this.dragIndex = -1;
      this.dragOverIndex = -1;
      this.dragReadyIndex = -1;
      this.clearLongPressTimer();
    },
    // 列表项播放/暂停按钮：正在播放该项时点击暂停；暂停后再次点击从暂停位置继续；点击其他项从头播放
    async onEndItemPlay(idx) {
      const a = this.$refs.endAudio;
      if (!a) return;
      if (idx === this.endIndex && this.endPlaying) {
        // 正在播放该项 → 暂停（保留进度）
        a.pause();
        this.endPlaying = false;
        this.activeContext.isPlaying = false;
      } else if (idx === this.endIndex) {
        // 暂停后再次点击同一项 → 从暂停位置继续
        try {
          await a.play();
          this.endPlaying = true;
          this.activeContext.isPlaying = true;
        } catch {
          this.$message.warning('播放失败，请检查文件格式');
        }
      } else {
        // 点击其他曲目 → 从头播放该项
        await this.playEndAt(idx);
      }
    },
    // 切换播放/暂停；若未开始则从第 0 首顺序播放
    async toggleEnd() {
      if (this.endPlaylist.length === 0) {
        this.$message.info('请先从乐曲库添加曲目到播放列表');
        return;
      }
      const a = this.$refs.endAudio;
      if (!a) return;
      if (this.endPlaying) {
        a.pause();
        this.endPlaying = false;
        this.activeContext.isPlaying = false;
      } else if (this.endIndex >= 0) {
        // 已开始过 → 恢复播放当前索引
        try {
          await a.play();
          this.endPlaying = true;
          this.activeContext.isPlaying = true;
        } catch {
          this.$message.warning('播放失败，请检查文件格式');
        }
      } else {
        // 从第 0 首开始顺序播放
        await this.playEndAt(0);
      }
    },
    // 播放指定索引的结束音乐
    async playEndAt(idx) {
      if (idx < 0 || idx >= this.endPlaylist.length) return;
      this.endIndex = idx;
      const a = this.$refs.endAudio;
      if (!a) return;
      const src = this.endPlaylistSrcs[idx];
      if (!src) {
        this.$message.error('文件读取失败');
        return;
      }
      // 切换 src（与原 src 不同时才更新，避免重复加载）
      if (a.src !== src) a.src = src;
      await this.$nextTick();
      await this.stopStart(true);
      try {
        a.currentTime = 0;
        await a.play();
        this.endPlaying = true;
        this.fadeIn(a);
        const track = this.endPlaylist[idx];
        this.recordHistory('end', track.name);
        this.setActivePlayer('end', track.name, a);
      } catch {
        this.$message.warning('播放失败，请检查文件格式');
      }
    },
    // 停止结束音乐；immediate=true 时跳过淡出（用于互斥切换）
    async stopEnd(immediate = false) {
      const a = this.$refs.endAudio;
      if (a) {
        if (this.endPlaying && !immediate) {
          await this.fadeOut(a);
        }
        this.clearFades(a);
        a.pause();
        a.currentTime = 0;
        a.volume = this.volume / 100;
      }
      this.endPlaying = false;
      this.endCurrent = 0;
      this.clearActivePlayer(a);
    },
    onEndMeta() {
      this.endDuration = this.$refs.endAudio.duration || 0;
      this.$refs.endAudio.volume = this.volume / 100;
    },
    onEndTime() {
      if (this.endPlaying) this.endCurrent = this.$refs.endAudio.currentTime;
    },
    onEndSeek(v) {
      if (this.$refs.endAudio) this.$refs.endAudio.currentTime = v;
    },
    onEndEnded() {
      this.endPlaying = false;
      this.endCurrent = 0;
      // 自动播放下一首（如果有）
      if (this.endIndex >= 0 && this.endIndex < this.endPlaylist.length - 1) {
        this.playEndAt(this.endIndex + 1);
      } else {
        // 全部播完
        this.endIndex = -1;
        this.clearActivePlayer(this.$refs.endAudio);
      }
    },

    // ===== 乐曲库管理 =====
    async removeTrack(row) {
      try {
        await this.$confirm(`确认删除曲目「${row.name}」？`, '提示', {
          type: 'warning',
          confirmButtonText: '删除',
          cancelButtonText: '取消'
        });
      } catch { return; }
      await dbDelete(row.id).catch(() => {});
      // 如果删除的曲目在当前抽取列表中，整体清理结束音乐
      const inList = this.endPlaylist.some(t => t.id === row.id);
      if (inList) {
        await this.stopEnd();
        this.endPlaylistSrcs.forEach(u => u && URL.revokeObjectURL(u));
        this.endPlaylistSrcs = [];
        this.endPlaylist = [];
        this.endIndex = -1;
      }
      this.library = this.library.filter(t => t.id !== row.id);
      writeLS(LS_META, this.library);
      this.$message.success('已删除');
    },
    async clearLibrary() {
      try {
        await this.$confirm('确认清空乐曲库？此操作不可恢复。', '清空确认', {
          type: 'warning',
          confirmButtonText: '清空',
          cancelButtonText: '取消'
        });
      } catch { return; }
      await dbClear().catch(() => {});
      if (this.endPlaylist.length > 0) {
        await this.stopEnd();
        this.endPlaylistSrcs.forEach(u => u && URL.revokeObjectURL(u));
        this.endPlaylistSrcs = [];
        this.endPlaylist = [];
        this.endIndex = -1;
      }
      this.library = [];
      writeLS(LS_META, []);
      this.$message.success('已清空乐曲库');
    },
    // 试听列表中的某首（淡入播放）
    async previewTrack(row) {
      // 试听时互斥立即停止主播放
      await Promise.all([this.stopStart(true), this.stopEnd(true)]);
      const blob = await dbGet(row.id);
      if (!blob) {
        this.$message.error('文件读取失败，请重新添加');
        return;
      }
      if (this.previewSrc) URL.revokeObjectURL(this.previewSrc);
      this.previewSrc = URL.createObjectURL(blob.blob);
      this.previewMeta = row;
      const a = this.$refs.previewAudio;
      if (!a) return;
      a.src = this.previewSrc;
      a.volume = 0;
      try {
        await a.play();
        this.fadeIn(a);
        this.setActivePlayer('preview', row.name, a);
        this.$message.info(`试听：${row.name}`);
      } catch {
        this.$message.warning('播放失败');
      }
    },
    // 试听结束（previewAudio 的 @ended 事件）
    onPreviewEnded() {
      const a = this.$refs.previewAudio;
      if (a) this.clearFades(a);
      this.clearActivePlayer(a);
      if (this.previewSrc) {
        URL.revokeObjectURL(this.previewSrc);
        this.previewSrc = null;
      }
      this.previewMeta = null;
    },

    // ===== 播放历史 =====
    recordHistory(type, name) {
      // 自动记录当前时段（按当前时间判定）
      const p = this.currentPeriod;
      this.history.unshift({
        type,
        name,
        periodKey: p ? p.key : null,
        periodLabel: p ? p.label : '—',
        playedAt: this.formatDateTime(new Date())
      });
      if (this.history.length > 10) this.history = this.history.slice(0, 10);
      writeLS(LS_HISTORY, this.history);
    },
    async clearHistory() {
      try {
        await this.$confirm('确认清空播放历史？', '提示', {
          type: 'warning',
          confirmButtonText: '清空',
          cancelButtonText: '取消'
        });
      } catch { return; }
      this.history = [];
      writeLS(LS_HISTORY, []);
      this.$message.success('已清空');
    },

    // ===== 错误处理 =====
    onAudioError(e) {
      // 无 src 或正在切换 src 时不报错
      const t = e && e.target;
      if (!t || !t.src || t.src === window.location.href) return;
      this.startPlaying = false;
      this.endPlaying = false;
      this.$message.error('音频加载失败，文件可能已损坏');
    },

    // ===== 格式化工具 =====
    // 时段对应的 Tag 颜色类型
    periodTagType(key) {
      return key === 'morning' ? 'warning' : key === 'noon' ? 'success' : 'info';
    },
    formatTime(sec) {
      if (!sec || !isFinite(sec)) return '00:00';
      const m = Math.floor(sec / 60);
      const s = Math.floor(sec % 60);
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    },
    formatDuration(sec) {
      if (!sec || !isFinite(sec)) return '--:--';
      return this.formatTime(sec);
    },
    // 格式化剩余时间（秒 → "mm:ss" 或 "HH:mm:ss"）；<=0 显示"已到结束时间"
    formatRemaining(sec) {
      if (!sec || sec <= 0) return '已到结束时间';
      sec = Math.floor(sec);
      const p = n => String(n).padStart(2, '0');
      if (sec >= 3600) {
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        const s = sec % 60;
        return `${p(h)}:${p(m)}:${p(s)}`;
      }
      const m = Math.floor(sec / 60);
      const s = sec % 60;
      return `${p(m)}:${p(s)}`;
    },
    formatSize(bytes) {
      if (!bytes) return '--';
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1024 / 1024).toFixed(2) + ' MB';
    },
    formatDateTime(d) {
      const p = n => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    }
  }
};
</script>

<style scoped>
.music-page {
  padding: 20px 24px 84px;     /* 底部留出固定播放条的空间 */
  max-width: 1400px;
  margin: 0 auto;
}
/* ===== 页头 ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.header-left > i {
  font-size: 2rem;
  color: #b45309;
  background: linear-gradient(135deg, rgba(180, 83, 9, 0.12), rgba(217, 119, 6, 0.06));
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.header-left h2 {
  margin: 0;
  font-size: 1.4rem;
  color: #1e293b;
  font-weight: 700;
}
.header-left .sub {
  font-size: 0.82rem;
  color: #94a3b8;
}
.header-right .badge {
  font-size: 0.78rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 6px 12px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.fade-settings {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid rgba(180, 83, 9, 0.2);
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  color: #92400e;
}
.fade-settings > i {
  color: #b45309;
}
.fade-label {
  font-weight: 600;
}
.fade-duration-input {
  width: 92px;
}
.fade-unit {
  color: #92400e;
}
/* ===== 卡片通用 ===== */
.card {
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}
.card >>> .el-card__header {
  padding: 14px 18px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}
.card-title > i {
  color: #b45309;
  font-size: 1.05rem;
}
/* ===== 主布局：CSS Grid（左上乐曲库 / 左下开始音乐 / 右侧结束音乐播放列表） ===== */
.main-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 20px;
  align-items: start;   /* 左侧两栏不强制等高 */
  margin-bottom: 20px;
}
.main-row .library-card {
  grid-column: 1;
  grid-row: 1;
}
.main-row .start-card {
  grid-column: 1;
  grid-row: 2;
}
.main-row .end-card {
  grid-column: 2;
  grid-row: 1 / span 2;   /* 右侧跨两行 */
}
@media (max-width: 768px) {
  .main-row {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
  }
  .main-row .library-card,
  .main-row .start-card,
  .main-row .end-card {
    grid-column: 1;
    grid-row: auto;
  }
}
/* ===== 空状态 ===== */
.empty-start,
.empty-library {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  text-align: center;
  gap: 8px;
}
.empty-start > i,
.empty-library > i {
  font-size: 2.4rem;
  color: #cbd5e1;
  margin-bottom: 8px;
}
.empty-start p,
.empty-library p {
  margin: 0;
  color: #64748b;
  font-size: 0.92rem;
}
.empty-library span {
  font-size: 0.78rem;
  color: #94a3b8;
  margin-bottom: 8px;
}
/* ===== 曲目信息 ===== */
.track-info {
  padding: 4px 0 14px;
}
/* ===== 调度开关 + 时钟 ===== */
.scheduler-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
}
.now-time {
  font-size: 0.78rem;
  color: #64748b;
  font-variant-numeric: tabular-nums;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 4px;
}
/* ===== 时段信息面板 ===== */
.period-info-panel {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
  border-left: 3px solid #d97706;
}
.info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  font-size: 0.88rem;
}
.info-row + .info-row {
  border-top: 1px dashed rgba(180, 83, 9, 0.15);
}
.info-label {
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 6px;
}
.info-label > i {
  font-size: 0.78rem;
  color: #b45309;
}
.info-value {
  color: #1e293b;
  font-weight: 600;
}
.info-value.playing {
  color: #16a34a;
}
/* ===== 结束音乐抽取信息面板 ===== */
.end-info-panel {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 14px;
  border-left: 3px solid #16a34a;
}
.end-info-panel .info-label {
  color: #15803d;
}
.end-info-panel .info-label > i {
  color: #16a34a;
}
.end-info-panel .info-value.urgent {
  color: #dc2626;
  font-weight: 700;
}
.end-info-panel .info-value.ended {
  color: #94a3b8;
  font-style: italic;
}
.end-info-panel .info-value.over {
  color: #dc2626;
  font-weight: 700;
}
/* ===== 结束音乐抽取列表 ===== */
.end-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
  max-height: 320px;
  overflow-y: auto;
}
.end-list-hint {
  margin: 0 0 4px 2px;
  font-size: 0.72rem;
  color: #94a3b8;
  display: flex;
  align-items: center;
  gap: 4px;
}
.end-list-hint > i {
  color: #94a3b8;
}
.end-list-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
}
.end-list-item:hover {
  border-color: #16a34a;
  background: #f0fdf4;
}
.end-list-item.active {
  border-color: #16a34a;
  background: linear-gradient(135deg, #f0fdf4 0%, #fff 60%);
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.12);
}
.end-idx {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
}
.end-list-item.active .end-idx {
  background: #16a34a;
  color: #fff;
}
.end-info {
  flex: 1;
  min-width: 0;
}
.end-name {
  font-size: 0.88rem;
  color: #1e293b;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.end-sub {
  font-size: 0.72rem;
  color: #94a3b8;
  margin-top: 2px;
}
.end-remove-btn {
  flex: 0 0 auto;
  margin-left: 4px;
  padding: 4px !important;
}
/* 列表项播放/暂停按钮（位于状态标签左侧） */
.end-play-btn {
  flex: 0 0 auto;
  padding: 3px 8px !important;
  margin-left: auto;
}
.end-play-btn:hover {
  color: #16a34a !important;
}
/* ===== 播放列表项：上下三角按钮 + 长按拖拽视觉反馈 ===== */
.end-order-btns {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  flex: 0 0 auto;
  margin-left: 2px;
}
.end-order-btn {
  width: 18px;
  height: 14px;
  line-height: 14px;
  padding: 0;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  font-size: 14px;
  transition: color 0.15s, background 0.15s;
  border-radius: 3px;
}
.end-order-btn:hover:not(:disabled) {
  color: #2563eb;
  background: #eff6ff;
}
.end-order-btn:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}
.end-list-item.drag-ready {
  border-color: #2563eb;
  background: linear-gradient(135deg, #eff6ff 0%, #fff 60%);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.18);
  cursor: grabbing;
}
.end-list-item.dragging {
  opacity: 0.45;
}
.end-list-item.drag-over {
  border-color: #2563eb;
  border-style: dashed;
  background: #eff6ff;
}
/* ===== 随机添加按钮（位于播放列表下方，蓝色，宽度同播放列表） ===== */
.end-random-add-btn {
  width: 100%;
  margin-top: 4px;
}
.period-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
}
.period-form-item {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px 4px;
  background: #fff;
  transition: all 0.2s;
}
.period-form-item.active {
  border-color: #d97706;
  background: linear-gradient(135deg, #fffbeb 0%, #fff 60%);
  box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.1);
}
.pf-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
  gap: 8px;
  flex-wrap: wrap;
}
.pf-hint {
  flex: 1;
  font-size: 0.72rem;
  color: #94a3b8;
  font-style: italic;
}
.pf-actions {
  display: flex;
  gap: 4px;
}
.pf-field {
  margin-bottom: 8px !important;
}
.pf-field >>> .el-form-item__label {
  font-size: 0.75rem;
  color: #64748b;
  padding: 0 8px 0 0;
  line-height: 1.6;
}
.pf-field >>> .el-form-item__content {
  line-height: 1.6;
}
.time-input {
  max-width: 120px;
}
.file-control {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  color: #475569;
  min-height: 28px;
}
.file-control > i {
  color: #b45309;
  font-size: 0.85rem;
}
.track-name-text {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1e293b;
  font-weight: 600;
}
.track-sub {
  color: #94a3b8;
  font-size: 0.72rem;
}
.no-track {
  color: #94a3b8;
  font-style: italic;
}
.track-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-radius: 8px;
  border-left: 3px solid #d97706;
}
.track-meta > i {
  font-size: 1.6rem;
  color: #d97706;
}
.track-name {
  flex: 1;
  overflow: hidden;
}
.name-text {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.name-sub {
  font-size: 0.75rem;
  color: #94a3b8;
  margin-top: 2px;
}
/* ===== 播放控件 ===== */
.player-controls {
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.progress-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.progress-bar {
  flex: 1;
}
.time-text {
  font-size: 0.75rem;
  color: #64748b;
  font-variant-numeric: tabular-nums;
  min-width: 42px;
  text-align: center;
}
.volume-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
}
.volume-wrap > i {
  color: #94a3b8;
  font-size: 0.9rem;
}
.volume-bar {
  flex: 1;
  max-width: 200px;
}
/* ===== 列表行 ===== */
.row-name {
  display: flex;
  align-items: center;
  gap: 8px;
}
.row-name > i {
  color: #b45309;
  font-size: 0.85rem;
}
/* ===== 乐曲库表格：曲目高度减少 80% ===== */
.library-table >>> .el-table td,
.library-table >>> .el-table th {
  padding: 0;
}
.library-table >>> .el-table .cell {
  padding: 1px 4px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.library-table >>> .el-table__body tr {
  height: auto;
}
.library-table .library-row-name {
  gap: 4px;
  font-size: 0.72rem;
  line-height: 16px;
}
.library-table .library-row-name > i {
  font-size: 0.64rem;
}
.library-table >>> .el-button--mini {
  padding: 0 3px;
  font-size: 0.68rem;
  line-height: 16px;
  height: 18px;
}
.library-table >>> td .cell {
  font-size: 0.7rem;
}
.library-table >>> .el-table::before {
  background-color: transparent;
  height: 0;
}
.library-table >>> .el-table th .cell {
  font-size: 0.72rem;
  line-height: 18px;
}
/* ===== 历史 ===== */
.history-card {
  margin-top: 4px;
}
.empty-history {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 30px 0;
  color: #94a3b8;
}
.history-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.history-name {
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;
}
.danger-text {
  color: #ef4444 !important;
}
.danger-text:hover {
  color: #dc2626 !important;
}
/* ===== 底部固定播放控制条 ===== */
.player-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1100;
  height: 64px;
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  color: #fff;
  display: flex;
  align-items: center;
  padding: 0 20px;
  gap: 20px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.25);
}
.bar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  min-width: 0;
  max-width: 280px;
}
.bar-track-name {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  white-space: nowrap;
}
.bar-track-name > i {
  color: #fbbf24;
  font-size: 0.9rem;
}
.bar-track-name > span {
  color: #f1f5f9;
  font-weight: 600;
  font-size: 0.9rem;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bar-center {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.bar-progress-wrap {
  flex: 1;
  min-width: 120px;
  display: flex;
  align-items: center;
}
.bar-progress {
  width: 100%;
}
.bar-time {
  font-size: 0.75rem;
  color: #cbd5e1;
  font-variant-numeric: tabular-nums;
  min-width: 42px;
  text-align: center;
}
.bar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
}
.bar-right > i {
  color: #cbd5e1;
  font-size: 0.9rem;
}
.bar-volume {
  width: 140px;
}
/* 滑入动画 */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}
.slide-up-enter,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
/* el-slider 在深色背景上的适配 */
.player-bar >>> .el-slider__runway {
  background-color: rgba(255, 255, 255, 0.18);
}
.player-bar >>> .el-slider__bar {
  background-color: #fbbf24;
}
.player-bar >>> .el-slider__button {
  border-color: #fbbf24;
  background-color: #fbbf24;
}
/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .music-page { padding: 12px; }
  .header-left h2 { font-size: 1.15rem; }
  .header-left > i { width: 44px; height: 44px; font-size: 1.5rem; }
  .header-right { display: none; }
  .progress-wrap { flex-wrap: wrap; }
  .history-name { max-width: 60vw; }
  .player-bar {
    height: auto;
    padding: 10px 12px;
    flex-wrap: wrap;
    gap: 8px;
  }
  .bar-left {
    max-width: 100%;
    flex: 1 1 100%;
  }
  .bar-center {
    flex: 1 1 100%;
    flex-wrap: wrap;
  }
  .bar-right { display: none; }
  .bar-volume { width: 100px; }
}
</style>
